import styles from "./pickOrUploadModal.module.scss";
import classnames from "classnames/bind";
import { useEffect, useLayoutEffect, useState } from "react";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { BaseImage, Image } from "../Image/Image";
import { AutoInputWithButton } from "../InputWithButton";
import { Upload, X } from "lucide-react";
import { Input } from "../Input";
import { get, isEmpty, noop } from "lodash";
import { uploadFileToCDN } from "lib/imageUtils";
import { LoaderWithText } from "../Loader";
import { useRecentlyUsedImages } from "contexts/database/useRecentlyUsedImages";
import { ConfirmableDeleteButton } from "../ConfirmableDeleteButton/ConfirmableDeleteButton";

const classNameBuilder = classnames.bind(styles);

const MODES = {
  UPLOAD: "UPLOAD",
  RECENTLY_USED: "RECENTLY_USED",
};

export const PickOrUploadModal = ({
  portalProps,
  onSubmit = noop,
  defaultImages,
  title = "Pick or Upload",
  initialValue,
  imageContainerClassName,
  imageClassName,
  buttonClassName,
  imageProps,
  defaultName = "Default",
  shouldDisableBackground,
  disableAutoFocusUpload,
  shouldDisableRecentlyUsed,
}) => {
  const { setIsPortalComponentRendered } = portalProps;
  const [newImageUrl, setNewImageUrl] = useState(initialValue);
  const [isUploading, setIsUploading] = useState(false);
  const [mode, setMode] = useState(MODES.UPLOAD);
  const { recentlyUsedImages, addRecentlyUsedImage, removeRecentlyUsedImage } =
    useRecentlyUsedImages();

  useEffect(() => {
    setNewImageUrl(initialValue);
  }, [initialValue, portalProps.isPortalComponentRendered]);

  useLayoutEffect(() => {
    if (mode === MODES.RECENTLY_USED && isEmpty(recentlyUsedImages)) {
      setMode(MODES.UPLOAD);
    }
  }, [mode, recentlyUsedImages]);

  const ImageComponent = shouldDisableBackground ? BaseImage : Image;

  const hasDefaultImages = defaultImages && !isEmpty(defaultImages);

  return (
    <Modal
      portalProps={portalProps}
      closeDrawer={() => setIsPortalComponentRendered(false)}
      title={title}
      contentClassName={styles["container"]}
      onFooterCancel={() => setIsPortalComponentRendered(false)}
      onFooterSubmit={() => {
        setIsPortalComponentRendered(false);
        onSubmit(newImageUrl);
      }}
      footerClassName={styles["footer"]}
      submitDisabled={!newImageUrl}
    >
      <div className={styles["preview"]}>
        {newImageUrl && (
          <ImageComponent
            className={classNameBuilder(
              "preview-image-container",
              imageContainerClassName
            )}
            imageClassName={classNameBuilder("preview-image", imageClassName)}
            src={newImageUrl || ""}
            alt="selected"
            contain
            {...imageProps}
          />
        )}
        {!newImageUrl && (
          <div className={classNameBuilder("or", "prompt")}>
            No Image Selected
          </div>
        )}
      </div>
      <div className={classNameBuilder("content")}>
        {!shouldDisableRecentlyUsed && !isEmpty(recentlyUsedImages) && (
          <div className={styles["header"]}>
            <Button
              className={classNameBuilder("header-button", {
                selected: mode === MODES.UPLOAD,
              })}
              onClick={() => setMode(MODES.UPLOAD)}
              colorful={mode === MODES.UPLOAD}
            >
              Upload
            </Button>
            <Button
              className={classNameBuilder("header-button", {
                selected: mode === MODES.RECENTLY_USED,
              })}
              colorful={mode === MODES.RECENTLY_USED}
              onClick={() => setMode(MODES.RECENTLY_USED)}
            >
              Recently Uploaded
            </Button>
          </div>
        )}
        {mode === MODES.UPLOAD ? (
          <div
            className={classNameBuilder("upload-content", {
              spread: mode === MODES.UPLOAD && !hasDefaultImages,
            })}
          >
            <div className={styles["title"]}>Upload your own</div>
            <AutoInputWithButton
              placeholder="Enter an image URL"
              onSubmit={(v) => {
                addRecentlyUsedImage(v);
                setNewImageUrl(v);
              }}
              reverse
              autoFocus={!disableAutoFocusUpload}
              buttonChildren={<Upload className={styles["upload-icon"]} />}
              containerClassName={styles["upload-input"]}
            />
            <div className={classNameBuilder("or")}>OR</div>
            <div className={classNameBuilder("title")}>Upload a File</div>
            {isUploading ? (
              <LoaderWithText centered>Please wait a moment...</LoaderWithText>
            ) : (
              <Input
                className={classNameBuilder("upload-input")}
                disabled={isUploading}
                type="file"
                accept="image/png, image/jpg"
                onChange={async (e) => {
                  let file = get(e, "target.files.0");
                  if (!file) {
                    return;
                  }

                  let cloudinaryUrl;
                  setIsUploading(true);
                  try {
                    cloudinaryUrl = await uploadFileToCDN(file);
                  } catch (err) {
                    setIsUploading(false);
                    console.error(err);
                    alert(err.message);

                    return;
                  }

                  setIsUploading(false);
                  addRecentlyUsedImage(cloudinaryUrl);
                  setNewImageUrl(cloudinaryUrl);
                }}
              />
            )}

            {hasDefaultImages && (
              <>
                <div className={classNameBuilder("or")}>OR</div>
                <div className={styles["title"]}>Pick A {defaultName}</div>
                <div className={styles["grid"]}>
                  {defaultImages.map((p, i) => {
                    return (
                      <Button
                        key={i}
                        icon
                        onClick={() => {
                          setNewImageUrl(p);
                        }}
                        className={classNameBuilder("button", buttonClassName)}
                      >
                        <ImageComponent
                          className={classNameBuilder("image")}
                          src={p}
                          alt="default"
                          {...imageProps}
                        />
                      </Button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className={styles["recently-uploaded-content"]}>
            <div className={styles["grid"]}>
              {recentlyUsedImages.map((p, i) => {
                return (
                  <div className={styles["recently-used-button"]}>
                    <Button
                      key={i}
                      icon
                      onClick={() => {
                        setNewImageUrl(p);
                      }}
                      className={classNameBuilder("button", buttonClassName)}
                    >
                      <ImageComponent
                        className={classNameBuilder("image")}
                        src={p}
                        alt="default"
                        {...imageProps}
                      />
                    </Button>
                    <ConfirmableDeleteButton
                      onConfirm={() => removeRecentlyUsedImage(p)}
                      buttonProps={{
                        className: styles["delete-button"],
                        round: true,
                        disableMovement: true,
                      }}
                    >
                      <X />
                    </ConfirmableDeleteButton>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
